import { useGetAllUsersQuery, useGetSimpleUsersByRolesQuery } from 'store/services/core/user';
import { useGetUsersInGroupQuery } from 'store/services/flow/userGroups';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchInput } from 'components/common/Inputs';
import { Table } from 'components/common/Table/Table';
import type { UserStatus } from 'types/enums.ts';
import { useTableQueryControls } from 'utils/hooks';

import { transformDataByDataSchema } from 'utils/common';
import { usersDataScheme as adminUsersScheme } from 'utils/table/admin/core/users';
import { usersDataScheme as simpleUsersScheme } from 'utils/table/user/users';
import { Box, styled } from '@mui/material';
import { DEFAULT_PAGE_SIZE } from '../../../utils/hooks/useTableQueryControls.ts';

const GridContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  width: '100%',
  gridTemplateColumns: 'repeat(1, 1fr)',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up('xl')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 0,
  },
}));

type Filters = {
  dateOfDeactivation: string | null;
  dateOfActivation: string | null;
  email: string;
  status: UserStatus[];
  fullName: string;
};

interface Props {
  groupId: string;
  searchValue?: string;
  roleIds?: string[];
  isMultiSelect?: boolean;
  hideCheckbox?: boolean;
  hideSearch?: boolean;
  defaultSelectedRows?: string[];
  fetchUsersMethod?: 'all' | 'simple';
  isLoading?: boolean;
  onSelectUser?: (users: string[]) => void;
  dataTestIdPrefix?: string;
}

type AnyUserSchema = typeof adminUsersScheme | typeof simpleUsersScheme;

export const ContactsTable = memo(
  ({
    groupId,
    searchValue = '',
    roleIds,
    isMultiSelect = true,
    hideCheckbox = true,
    hideSearch = false,
    defaultSelectedRows,
    fetchUsersMethod = 'all',
    isLoading,
    onSelectUser,
    dataTestIdPrefix,
  }: Props) => {
    const { t } = useTranslation();
    const tableQueryKey = `${fetchUsersMethod === 'all' ? 'contactUsers' : 'groupModal'}-${groupId || 'all'}`;

    const { state, setPagination, setSort, setSearch, getPageSize } = useTableQueryControls<AnyUserSchema, Filters>(
      tableQueryKey,
      {
        filters: {
          dateOfDeactivation: null,
          dateOfActivation: null,
          email: '',
          status: [],
          fullName: '',
        },
        sort: { by: 'fullName', order: 'asc' },
        search: searchValue || '',
        pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
      },
      {
        syncPaginationWithUrl: false,
      }
    );

    const { data: allUsers, isLoading: allUsersLoading } = useGetAllUsersQuery(
      {
        DateOfDeactivation: (state?.filters.dateOfDeactivation as string) || '',
        DateOfActivation: (state?.filters.dateOfActivation as string) || '',
        Email: state?.filters.email as string,
        Status: (state?.filters.status as UserStatus[]) || [],
        FullName: state?.filters.fullName as string,
        page: state?.pagination.page ?? 1,
        pageSize: getPageSize(),
        orderBy: state?.sort?.by ?? 'fullName',
        orderDesc: state?.sort?.order === 'desc',
        search: hideSearch ? searchValue : (state?.search ?? ''),
      },
      { skip: fetchUsersMethod === 'simple', refetchOnMountOrArgChange: true }
    );

    const { data: allSimpleUsers, isFetching: allSimpleUsersLoading } = useGetSimpleUsersByRolesQuery(
      {
        page: state?.pagination.page ?? 1,
        pageSize: getPageSize(),
        orderBy: state?.sort?.by ?? 'fullName',
        orderDesc: state?.sort?.order === 'desc',
        search: hideSearch ? searchValue : (state?.search ?? ''),
        ids: roleIds ?? [],
      },
      {
        skip: fetchUsersMethod === 'all',
        refetchOnMountOrArgChange: true,
      }
    );
    const { data: usersInGroup, isLoading: usersInGroupLoading } = useGetUsersInGroupQuery(
      {
        page: state?.pagination.page ?? 1,
        pageSize: getPageSize(),
        orderBy: state?.sort?.by ?? 'fullName',
        orderDesc: state?.sort?.order === 'desc',
        search: hideSearch ? searchValue : (state?.search ?? ''),
        userGroupId: groupId,
        roleIds,
      },
      {
        skip: groupId === '',
        refetchOnMountOrArgChange: true,
      }
    );

    const data = groupId === '' ? (fetchUsersMethod === 'all' ? allUsers : allSimpleUsers) : usersInGroup;

    const activeDataSchema = useMemo(() => {
      if (groupId !== '') {
        return adminUsersScheme;
      }
      return simpleUsersScheme;
    }, [groupId]);

    const transformedData = useMemo(() => {
      const transformedUsers = data?.items?.map((user) => ({
        ...user,
        fullName: user.fullName,
        organization: user.organization ? [user.organization] : '',
      }));

      return transformDataByDataSchema(transformedUsers, activeDataSchema);
    }, [activeDataSchema, data?.items]);

    useEffect(() => {
      setPagination({
        page: 1,
        pageSize: data?.pageSize || DEFAULT_PAGE_SIZE,
      });
    }, [data?.pageSize, groupId, setPagination]);

    return (
      <Box width="100%">
        <Table
          dataTestIdPrefix={dataTestIdPrefix}
          page={data?.page}
          dataSchema={activeDataSchema}
          data={transformedData}
          rows={data?.totalCount ?? 0}
          isLoading={allUsersLoading || allSimpleUsersLoading || usersInGroupLoading || isLoading}
          pageSize={data?.pageSize || DEFAULT_PAGE_SIZE}
          hideCheckbox={hideCheckbox}
          isMultiSelect={isMultiSelect}
          defaultSelectedRows={defaultSelectedRows}
          onSelectRow={onSelectUser}
          onPageSizeChange={(pageSize) =>
            setPagination({
              page: 1,
              pageSize: pageSize,
            })
          }
          onCurrentPageChange={(page) =>
            setPagination({
              page: page,
              pageSize: data?.pageSize ?? DEFAULT_PAGE_SIZE,
            })
          }
          onSort={({ column, direction }) =>
            setSort({
              by: column,
              order: direction,
            })
          }
          sort={{
            column: state?.sort?.by ?? 'id',
            direction: state?.sort?.order ?? 'asc',
          }}
          controls={
            !hideSearch && (
              <GridContainer>
                <SearchInput
                  search={state?.search}
                  onChange={setSearch}
                  placeholder={t('common.search.users')}
                  data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-search-input` : undefined}
                />
              </GridContainer>
            )
          }
        />
      </Box>
    );
  }
);
